import { useEffect, useRef, useState } from 'react';
import cn from 'clsx';
import { useAtom } from 'jotai';
import { makeStyles } from '@material-ui/styles';
import { Theme } from '@bizzabo/design-system';

import { IsExpandedAppTool, SelectedAppToolAtom } from '../../atoms/appToolsAtom';

import AppView from '../AppView/AppView';
import AppToolbar from '../AppToolbar/AppToolbar';
import AppToolbarItem from '../AppToolbarItem/AppToolbarItem';
import { AppToolId, IAppToolConfig } from '../../types/appTools';
import useTypeTracking from '../../../analytics';
import useShowMeeting from '../../decorators/useShowMeeting';
import useOnClickOutside from '../../../hooks/useOnClickOutside';
import { getMain } from '../../../agenda/helpers/layout';
import useIsMobile from '../../../hooks/useIsMobile';
import useDefaultAppTool from '../../decorators/useDefaultAppTool';
import { getMobileHeaderNavCloseButton } from '../../../utils/layoutsElements';
import useInnerHeight from '../../../hooks/useInnerHeight';
import useSessionIdFromRoute from '../../../hooks/useSessionIdFromRoute';

const isClickInMainRef = event => {
    const main = getMain();
    return main?.contains(event.target);
};

const isClickOnNavClose = event => {
    const navCloseButton = getMobileHeaderNavCloseButton();
    return navCloseButton?.contains(event.target);
};

const AppToolsView = ({ appToolbarItems }) => {
    const [isExpanded, setIsExpanded] = useAtom(IsExpandedAppTool);
    const [selectedApp, setSelectedApp] = useAtom(SelectedAppToolAtom);
    const innerHeight = useInnerHeight();

    const [showExpandAnimation, setShowExpandAnimation] = useState<boolean>(false);
    const [showCollapseAnimation, setShowCollapseAnimation] = useState<boolean>(false);
    const { sessionId } = useSessionIdFromRoute();

    useDefaultAppTool();

    // TODO: should extract it outside app tool
    useShowMeeting(selectedApp, setSelectedApp, isExpanded, setIsExpanded);

    const isMobile = useIsMobile();
    const classes = useStyles({ innerHeight });

    const { Track, trackClickEvent } = useTypeTracking({
        data: {
            category: 'VENUE_TOOL_BAR',
        },
    });

    const ref = useRef();
    useOnClickOutside(ref, e => {
        if (isExpanded && isMobile && isClickInMainRef(e) && !isClickOnNavClose(e)) {
            doCollapse();
        }
    });

    const onAppSelected = app => {
        if (!isExpanded) {
            onExpand(app);
        } else {
            app !== selectedApp ? onExpand(app, false) : onCollapse();
        }

        if (isExpanded && app === selectedApp) {
            trackCollapse();
        }
    };

    useEffect(() => {
        if (isMobile) {
            setIsExpanded(false);
        }
    }, [isMobile]);

    const onExpand = (app: AppToolId, withAnimation = true) => {
        if (withAnimation && !isMobile) {
            setShowExpandAnimation(true);
            doExpand(app);

            setTimeout(() => {
                setShowExpandAnimation(false);
            }, 500);
        } else {
            doExpand(app);
        }
    };

    const doExpand = app => {
        setIsExpanded(true);
        setSelectedApp(app);
    };

    const onCollapse = () => {
        if (!isMobile) {
            setShowCollapseAnimation(true);

            setTimeout(() => {
                doCollapse();
                setShowCollapseAnimation(false);
            }, 500);
        } else {
            doCollapse();
        }
    };

    const doCollapse = () => {
        setIsExpanded(false);
        trackCollapse();
    };

    const trackCollapse = () => {
        trackClickEvent({
            subCategory: 'CLOSE_TOOL_BAR',
        });
    };

    const trackIconClick =
        (appTool: IAppToolConfig) => (app: string, shouldShowNotification: boolean) => {
            trackClickEvent({
                subCategory: 'OPEN_ICON',
                description: {
                    icon_clicked: app,
                    is_notifications: shouldShowNotification,
                    location: 'VENUE_TOOL_BAR',
                    ...(sessionId && { session_id: sessionId }),
                    ...(appTool.category && { app_category: appTool.category }),
                    ...(appTool.name && { app_name: appTool.name }),
                },
            });
        };

    return (
        <div
            className={cn(classes.container, {
                [classes.expanded]: isExpanded,
                [classes.isChildCollapsing]: showCollapseAnimation && !isMobile,
            })}
            ref={ref}
        >
            <div
                className={cn(classes.appView, {
                    [classes.hideAppView]: !isExpanded,
                    [classes.expandPanelAnimation]: showExpandAnimation && !isMobile,
                    [classes.collapsePanelAnimation]: showCollapseAnimation && !isMobile,
                })}
            >
                <AppView
                    appToolbarItems={appToolbarItems}
                    selectedApp={selectedApp}
                    isExpanded={isExpanded}
                    shouldShowCollapseAnimation={showCollapseAnimation}
                    shouldShowExpandAnimation={showExpandAnimation}
                />
            </div>
            <Track>
                <AppToolbar
                    isExpanded={isExpanded}
                    onCollapse={onCollapse}
                    showExpandAnimation={showExpandAnimation}
                    showCollapseAnimation={showCollapseAnimation}
                >
                    {appToolbarItems.map(appToolbarItem => {
                        return (
                            <appToolbarItem.isEnabled
                                key={appToolbarItem.id}
                                appTool={appToolbarItem.id}
                            >
                                {({ isEnabled }) =>
                                    !!isEnabled && (
                                        <div className={classes.item}>
                                            <AppToolbarItem
                                                id={appToolbarItem.id}
                                                icon={appToolbarItem.icon}
                                                animatedIcon={appToolbarItem.animatedIcon}
                                                iconUrl={appToolbarItem.iconUrl}
                                                title={appToolbarItem.title}
                                                isSelected={
                                                    isExpanded && selectedApp === appToolbarItem.id
                                                }
                                                awareness={appToolbarItem.awareness}
                                                onAppSelected={onAppSelected}
                                                useNotification={appToolbarItem.useNotification}
                                                trackIconClick={trackIconClick(appToolbarItem)}
                                            />
                                        </div>
                                    )
                                }
                            </appToolbarItem.isEnabled>
                        );
                    })}
                </AppToolbar>
            </Track>
        </div>
    );
};

const useStyles = makeStyles<Theme, { innerHeight: number }>(
    theme => ({
        '@keyframes moveToRight': {
            from: {
                opacity: 1,
            },
            to: {
                transform: 'translateX(400)',
                width: 100,
                maxWidth: 100,
                opacity: 0,
                pointerEvents: 'none',
            },
        },
        '@keyframes moveToLeft': {
            from: {
                width: 100,
                opacity: 0,
            },
            to: {
                width: 500,
                maxWidth: 500,
                opacity: 1,
            },
        },
        container: {
            display: 'flex',
            height: '100vh',
            alignItems: 'end',
            justifyContent: 'flex-end',
            flexDirection: 'row',

            [theme.breakpoints.up('sm')]: {
                height: ({ innerHeight }) => `${innerHeight}px`,
            },

            [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                position: 'fixed',
                width: '100%',
                bottom: 0,
                height: 70,
                zIndex: 99999,
                right: 0,
            },
        },
        item: {
            paddingBottom: 16,
            [theme.breakpoints.down('sm')]: {
                paddingBottom: 0,
            },
        },
        expanded: {
            width: '33vw',
            minWidth: 475,
            maxWidth: 600,
            transition: 'width 0.5s',
            [theme.breakpoints.down('sm')]: {
                height: '85%',
                right: 0,
                minWidth: '100%',
            },
        },
        isChildCollapsing: {
            width: 100,
            minWidth: 100,
            maxWidth: 100,
        },
        appView: {
            height: '100%',
            width: 'calc(100% - 100px)',
            [theme.breakpoints.down('sm')]: {
                height: 'calc(100% - 63px)',
                width: '100%',
            },
        },
        hideAppView: {
            display: 'none',
        },
        expandPanelAnimation: {
            display: 'block',
            width: 100,
            maxWidth: 100,

            animation: '$moveToLeft 0.3s',
            animationFillMode: 'forwards',
        },
        collapsePanelAnimation: {
            display: 'block',
            width: '100%',
            maxWidth: '100%',

            animation: '$moveToRight 0.3s',
            animationFillMode: 'forwards',
        },
    }),
    {
        classNamePrefix: 'AppToolsView',
    }
);

export default AppToolsView;
